/**
 * Utilitaires partagés entre les composants du traducteur IA.
 */

/**
 * Arrondit un score à 2 décimales.
 * @param {number} score
 * @returns {number}
 */
export function roundScore(score) {
    return Math.round(score * 100) / 100
}

/**
 * Retourne la classe CSS Bootstrap du badge selon le score de traduction.
 * Arrondit d'abord le score à 2 décimales avant les comparaisons.
 * @param {number} score
 * @returns {string}
 */
export function badgeClass(score) {
    const roundedScore = roundScore(score)
    
    if (roundedScore >= 0.9) return 'badge-success'
    if (roundedScore >= 0.8) return 'badge-info'
    if (roundedScore >= 0.5) return 'badge-warning'
    return 'badge-danger'
}

/**
 * Formate un score sur 2 décimales (locale FR).
 * @param {number} score
 * @returns {string}
 */
export function formatScore(score) {
    if (score === null || score === undefined || Number.isNaN(Number(score))) return ''
    return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(score))
}

/**
 * Retourne le HTML du texte traduit avec les termes mis en surbrillance.
 * Échappe le HTML avant d'injecter les balises <mark>.
 * @param {string} translatedText
 * @param {Array<{start: number, end: number}>} highlights
 * @returns {string}
 */
export function buildHighlightedHtml(translatedText, highlights) {
    const escapeHtml = s => s
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
    const rawText = String(translatedText || '')
    if (!Array.isArray(highlights) || !highlights.length) return escapeHtml(rawText)

    const textLength = rawText.length
    const ranges = highlights
        .map(h => ({
            start: Number(h?.start),
            end: Number(h?.end)
        }))
        .filter(r => Number.isInteger(r.start) && Number.isInteger(r.end))
        .map(r => ({
            start: Math.max(0, Math.min(r.start, textLength)),
            end: Math.max(0, Math.min(r.end, textLength))
        }))
        .filter(r => r.end > r.start)
        .sort((a, b) => (a.start - b.start) || (a.end - b.end))

    if (!ranges.length) return escapeHtml(rawText)

    let cursor = 0
    let html = ''
    for (const range of ranges) {
        if (range.end <= cursor) continue
        const start = Math.max(range.start, cursor)
        if (start > cursor) {
            html += escapeHtml(rawText.slice(cursor, start))
        }
        html += `<mark class="highlight-term">${escapeHtml(rawText.slice(start, range.end))}</mark>`
        cursor = range.end
    }
    if (cursor < rawText.length) {
        html += escapeHtml(rawText.slice(cursor))
    }
    return html
}
